import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { RatesShell, RatesPage } from "../../site/RatesChrome";
import { ArrowRight, Star, Shield, Trophy, Home, RefreshCw, Check, Calendar, TrendingUp, TrendingDown, Minus, Scale, MapPin, Building2, Eye, Compass } from "lucide-react";
import SeoHead, { LOCAL_BUSINESS_SCHEMA } from "./SeoHead";
import MarketSignalCard from "./MarketSignalCard";
import FeaturedRates from "./FeaturedRates";
import FunnelDrawer from "./FunnelDrawer";
import { publicApi, fmtRate, minutesAgo } from "./api";
import { loadFunnelSession } from "./funnelSession";
import { captureRefFromUrl, withRef } from "./partnerRef";
import PublicFooter from "./PublicFooter";

const SCROLL_TARGET = "featured-rates-anchor";

// Detect embed mode from the URL once at module load.
// `?embed=core` → core conversion engine only (hero + featured + signal + how-it-works).
// `?embed=full` or no param → standalone site experience.
const DEMO_RATES = { lender_count: 26, updated_at_iso: new Date().toISOString(), cards: [
  { term: "5yr fixed", label: "5-Year Fixed", rate: 3.94, risk: "volatile", tier_label: "Top Canadian Lender", channel_label: "Broker Channel · Best Available Today", available: true },
  { term: "5yr variable", label: "5-Year Variable", rate: 3.7, risk: "stable", tier_label: "Big-6 Bank", channel_label: "Broker Channel · Best Available Today", available: true },
  { term: "3yr fixed", label: "3-Year Fixed", rate: 3.89, risk: "stable", tier_label: "Monoline Lender", channel_label: "Broker Channel · Best Available Today", available: true },
] };
const DEMO_DESK = (d) => ({ ...(d || {}), comparison: { fixed: { rate: 3.94, label: "5-Year Fixed", monthly_payment: 2612 }, variable: { rate: 3.7, label: "5-Year Variable", monthly_payment: 2547 }, reference_loan: 500000, amort_years: 25, monthly_savings: 65, cheaper_today: "variable" },
  signal: { lean: "FIXED", headline: "Variable is cheaper today, but bond yields just jumped 21 bps in five days—fixed pricing won't sit here long.", reasons: ["5-yr bond up 0.21% this week", "Bank of Canada on hold until Oct 28", "Fixed-variable gap is only 0.24%"] } });

function readEmbedMode() {
  if (typeof window === "undefined") return null;
  const p = new URLSearchParams(window.location.search);
  return p.get("embed");
}

// Optional `?city=sherwood-park` URL param — supplied by `embed.js` when
// the host script tag carries `data-city`. Used to city-tokenize the
// hero copy / SEO meta. Falls back to "Alberta" defaults when absent so
// every existing standalone / iframe install keeps behaving identically.
const CITY_LABEL_OVERRIDES = {
  "sherwood-park":       "Sherwood Park",
  "st-albert":           "St. Albert",
  "fort-saskatchewan":   "Fort Saskatchewan",
  "spruce-grove":        "Spruce Grove",
  "grande-prairie":      "Grande Prairie",
  "medicine-hat":        "Medicine Hat",
  "red-deer":            "Red Deer",
  "edmonton":            "Edmonton",
  "calgary":             "Calgary",
  "lethbridge":          "Lethbridge",
  "leduc":               "Leduc",
  "airdrie":             "Airdrie",
  "okotoks":             "Okotoks",
  "cochrane":            "Cochrane",
  "alberta":             "Alberta",
};
export function readCityFromUrl() {
  if (typeof window === "undefined") return null;
  const raw = new URLSearchParams(window.location.search).get("city");
  if (!raw) return null;
  const slug = raw.trim().toLowerCase().replace(/_/g, "-");
  if (!slug) return null;
  if (CITY_LABEL_OVERRIDES[slug]) return CITY_LABEL_OVERRIDES[slug];
  // Title-case fallback for new-town slugs we haven't seen yet.
  return slug
    .split("-")
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

export default function Widget() {
  const navigate = useNavigate();
  const [signal, setSignal] = useState(null);
  const [signalLoading, setSignalLoading] = useState(true);
  const [rates, setRates] = useState(null);
  const [ratesLoading, setRatesLoading] = useState(true);
  const [desk, setDesk] = useState(null);
  const [deskLoading, setDeskLoading] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerConfig, setDrawerConfig] = useState({ mode: "full" });
  const embedMode = useMemo(readEmbedMode, []);
  const isEmbed = embedMode === "core";
  const cityLabel = useMemo(readCityFromUrl, []);
  const rootRef = useRef(null);
  // Hello skin: new-site chrome (header/footer/brand) around the same data + funnel. Live on / and /rates since 2026-09-13; the legacy chrome only survives inside the core embed.
  const { pathname } = useLocation();
  const previewMode = pathname === "/rates-preview";
  const hello = !isEmbed;
  // ?demo=1 on the preview route only: sample rates so the design can be reviewed where no lender feed is synced.
  const demo = previewMode && new URLSearchParams(window.location.search).get("demo") === "1";

  // Partner Hub — capture ?ref=slug from the URL once on mount so any
  // downstream lead submit (this page or after navigating to /compare-all,
  // etc.) carries the attribution. Idempotent.
  useEffect(() => {
    captureRefFromUrl();
  }, []);

  // Auto-resume — if the visitor previously completed the funnel and we have
  // a saved session in localStorage (within TTL), bounce them straight to
  // Compare instead of showing the home widget. This survives parent-page
  // refreshes that wipe the iframe's in-memory route.
  //
  // CRITICAL: only fire this ONCE per browser-tab session. Without the
  // sessionStorage guard, the effect hijacks the browser back button —
  // a user on /compare hitting back lands on `/`, the effect runs,
  // navigate(replace: true) bounces them right back to /compare, making
  // back appear broken (a "trapped on compare" loop). The intended use
  // case (iframe parent-page refresh recovery) starts a fresh tab/iframe
  // load with empty sessionStorage, so the guard never blocks legitimate
  // resumes — only the back-button trap.
  useEffect(() => {
    const SESSION_RESUMED_KEY = "hm_funnel_resumed";
    let alreadyResumedThisSession = false;
    try {
      alreadyResumedThisSession = sessionStorage.getItem(SESSION_RESUMED_KEY) === "1";
    } catch {
      // sessionStorage can throw in some private/embedded contexts —
      // treat as "not yet resumed" and proceed (worst case: one resume).
    }
    if (alreadyResumedThisSession) return;

    const saved = loadFunnelSession();
    if (!saved || !saved.leadId) return;
    // Switch-to-Fixed has its own results page — never resume it into /compare.
    if (saved.query?.intent === "switch_to_fixed") return;

    try {
      sessionStorage.setItem(SESSION_RESUMED_KEY, "1");
    } catch {
      // ignore — see comment above
    }

    const params = new URLSearchParams(saved.query || { lead: saved.leadId });
    // Preserve embed mode through the redirect so the Compare page stays
    // inside the iframe styling.
    if (embedMode) params.set("embed", embedMode);
    navigate(`/compare?${params.toString()}`, { replace: true });
  }, [navigate, embedMode]);

  const loadAll = useCallback(async () => {
    // Fire all three in PARALLEL — the rate tiles must not wait behind the
    // market-signal call (they used to load sequentially, which stacked the
    // latency and made rates appear slow on first paint). Each request keeps
    // its own loading flag so every section renders the moment its own data
    // arrives.
    setSignalLoading(true);
    setRatesLoading(true);
    setDeskLoading(true);

    const pSignal = publicApi
      .get("/market-signal")
      .then((s) => setSignal(s.data))
      .catch((e) => console.warn("market-signal load failed", e))
      .finally(() => setSignalLoading(false));

    const pRates = publicApi
      .get("/featured-rates")
      .then((r) => setRates(r.data))
      .catch((e) => console.warn("featured-rates load failed", e))
      .finally(() => setRatesLoading(false));

    const pDesk = publicApi
      .get("/decision-desk")
      .then((d) => setDesk(d.data))
      .catch((e) => console.warn("decision-desk load failed", e))
      .finally(() => setDeskLoading(false));

    await Promise.allSettled([pSignal, pRates, pDesk]);
  }, []);

  useEffect(() => {
    loadAll();
    // Refresh data every 5 minutes (live feel)
    const intv = setInterval(loadAll, 300000);
    return () => clearInterval(intv);
  }, [loadAll]);

  // Force-white surface when embedded so the iframe sits cleanly inside any
  // parent page (the global admin theme is dark; we don't want that bleeding
  // into the iframe's body background).
  useEffect(() => {
    if (!isEmbed || typeof document === "undefined") return undefined;
    const root = document.documentElement;
    const body = document.body;
    const appEl = document.querySelector(".App");
    const prevHtmlBg = root.style.background;
    const prevBodyBg = body.style.background;
    const prevAppBg = appEl ? appEl.style.background : "";
    root.style.background = "#FFFFFF";
    body.style.background = "#FFFFFF";
    if (appEl) appEl.style.background = "#FFFFFF";
    body.classList.add("hello-embed-mode");
    return () => {
      root.style.background = prevHtmlBg;
      body.style.background = prevBodyBg;
      if (appEl) appEl.style.background = prevAppBg;
      body.classList.remove("hello-embed-mode");
    };
  }, [isEmbed]);

  // When embedded inside an iframe, broadcast height so the parent loader
  // (embed.js) can size the iframe to fit content — no scrollbars, no clipped UI.
  useEffect(() => {
    if (!isEmbed || typeof window === "undefined") return undefined;
    const post = () => {
      const h = Math.max(
        rootRef.current?.scrollHeight || 0,
        document.documentElement?.scrollHeight || 0,
        document.body?.scrollHeight || 0,
      );
      try {
        window.parent.postMessage(
          { type: "hello-mortgage-widget-height", height: h, ts: Date.now() },
          "*"
        );
      } catch (e) {
        /* parent unreachable — silently ignore */
      }
    };
    post();
    const ro = new ResizeObserver(() => post());
    if (rootRef.current) ro.observe(rootRef.current);
    if (document.body) ro.observe(document.body);
    const interval = setInterval(post, 1500);
    window.addEventListener("load", post);
    return () => {
      ro.disconnect();
      clearInterval(interval);
      window.removeEventListener("load", post);
    };
  }, [isEmbed]);

  const openDrawer = (cfg = { mode: "full" }) => {
    setDrawerConfig(cfg);
    setDrawerOpen(true);
  };

  // When the embed widget on a partner site (hellomortgage.ca/sherwoodparkrates)
  // links into us with a `?persona=buying|renew_refi`, `?intent=switch_to_fixed`,
  // or `?rate_term=5-year-fixed` query param, auto-open the matching drawer on
  // mount so the visitor lands directly inside the funnel instead of the
  // standalone landing screen. Fires exactly once per page load.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const p = new URLSearchParams(window.location.search);
    const persona = (p.get("persona") || "").toLowerCase();
    const intent = (p.get("intent") || "").toLowerCase();
    const rateTerm = (p.get("rate_term") || "").toLowerCase();
    if (rateTerm) {
      // Look up the matching featured card so the inquire drawer can show
      // the term label + rate. We wait for `rates` to load before opening.
      if (!rates) return;
      const card = (rates.cards || []).find(
        (c) => (c.label || "").toLowerCase().replace(/\s+/g, "-") === rateTerm,
      );
      if (card) {
        openDrawer({ mode: "inquire", inquireContext: card });
        return;
      }
    }
    if (intent === "switch_to_fixed") {
      openDrawer({ mode: "full", presetIntent: "switch_to_fixed" });
      return;
    }
    if (persona === "buying" || persona === "renew_refi") {
      openDrawer({ mode: "full", presetLevel1: persona });
    }
    // Intentionally NOT in the deps array beyond `rates` — we only react
    // to the URL params once at mount (and once more when rates land, to
    // hydrate the inquire card lookup).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rates]);

  const scrollToFeatured = () => {
    const el = document.getElementById(SCROLL_TARGET);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  // Headline rate = lowest available across the 3 featured cards
  const headlineRate = useMemo(() => {
    const cards = (rates?.cards || []).filter((c) => c.available && c.rate != null);
    if (cards.length === 0) return null;
    return cards.reduce((a, b) => (a.rate <= b.rate ? a : b));
  }, [rates]);

  // ---------------- SEO meta (homepage) ----------------
  const regionPhrase = cityLabel || "Alberta";
  const homepageTitle = cityLabel
    ? `Today's Best Mortgage Rates in ${cityLabel} · Hello Mortgage`
    : hello
      ? "Alberta Mortgage Rates | Current Alberta Mortgage Rates | Hello Mortgage"
      : "Today's Best Alberta Mortgage Rates · Hello Mortgage";
  const bestFixed = rates?.cards?.find?.((c) => c.term === "5yr fixed")?.rate;
  const bestVariable = rates?.cards?.find?.((c) => c.term === "5yr variable")?.rate;
  const rateBits = [bestFixed ? `5-year fixed from ${Number(bestFixed).toFixed(2)}%` : "", bestVariable ? `variable from ${Number(bestVariable).toFixed(2)}%` : ""].filter(Boolean);
  const homepageMeta = `Current ${regionPhrase} mortgage rates${rateBits.length ? `: ${rateBits.join(", ")}` : ""}. Compare 50+ lenders and monthly payments. Updated daily.`;

  return (
    <>
      {!isEmbed && (
        <SeoHead
          title={homepageTitle}
          description={homepageMeta}
          path={hello ? "/rates/" : "/"}
          siteUrl={hello ? "https://www.hellomortgage.ca" : undefined}
          ogImage={hello ? "https://www.hellomortgage.ca/brand/hello-mortgage-team-brx-logo.png" : undefined}
          schema={hello ? [] : LOCAL_BUSINESS_SCHEMA}
          noindex={previewMode}
        />
      )}
      {/* Legacy chrome only: fallback h1 for crawlers. The Hello chrome renders a single visible keyword H1. */}
      {!hello && <header className="sr-only">
        <h1>{cityLabel ? `Live ${cityLabel} Mortgage Rates · Hello Mortgage` : "Live Alberta Mortgage Rates · Hello Mortgage"}</h1>
        <p>
          Real-time mortgage rate intelligence sourced live from across Canada.
          Fixed and variable rates, market direction signals, and a personalized
          strategy from a mortgage broker who watches the market for you.
        </p>
      </header>}

      {hello ? (
        <RatesShell>
          <div ref={rootRef} data-testid="public-widget-root" data-embed-mode="hello">
            <RatesPage cityLabel={cityLabel} headlineRate={demo ? DEMO_RATES.cards[1] : headlineRate} rates={demo ? DEMO_RATES : rates} ratesLoading={demo ? false : ratesLoading} signal={signal} desk={demo ? DEMO_DESK(desk) : desk}
              openDrawer={openDrawer} onCompareAll={() => navigate(withRef("/compare-all"))} />
          </div>
        </RatesShell>
      ) : (
      <main
        ref={rootRef}
        className={`${isEmbed ? "bg-white min-h-screen" : "bg-[#F8FAFC] min-h-screen"} font-body text-brand-navy antialiased`}
        data-testid="public-widget-root"
        data-embed-mode={embedMode || "standalone"}
      >
        <div className={`max-w-6xl mx-auto px-5 sm:px-8 ${isEmbed ? "py-6 sm:py-8" : "py-8 sm:py-12"} space-y-10 sm:space-y-14`}>
          {/* Hero — RateHub-style giant rate + persona split */}
          <section className="animate-fade-up text-center" data-testid="hero-section">
            <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-brand-red mb-4 font-body">
              {cityLabel ? `${cityLabel} Mortgage Rates · Live` : "Alberta Mortgage Rates · Live"}
            </div>

            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-extrabold tracking-tighter leading-[0.92] text-brand-navy font-display">
              Today's best rate<span className="text-brand-red">.</span>
            </h1>

            {/* Giant rate */}
            <div className="mt-6 sm:mt-8 flex flex-wrap items-baseline justify-center gap-x-4 gap-y-2">
              <span
                className="text-7xl sm:text-8xl lg:text-9xl font-bold tracking-tighter font-display tabular-nums text-brand-navy leading-none"
                data-testid="hero-headline-rate"
              >
                {ratesLoading ? (
                  <span className="inline-block w-48 h-20 bg-slate-200 rounded animate-pulse" />
                ) : (
                  fmtRate(headlineRate?.rate)
                )}
                <span className="text-brand-red">*</span>
              </span>
              <span className="text-base sm:text-xl text-slate-500 font-body">
                {headlineRate?.label || "5-Year Variable"}
              </span>
            </div>

            <div className="mt-2 text-[11px] text-slate-400 font-body" data-testid="hero-headline-meta">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-signal-stable animate-live-pulse mr-1.5 align-middle motion-reduce:animate-none" />
              Lowest of {rates?.lender_count || "26"} live lender rates · Updated{" "}
              {minutesAgo(rates?.updated_at_iso) || "every business day"}
            </div>

            <p className="mt-6 sm:mt-7 text-base sm:text-lg text-slate-600 font-body max-w-2xl mx-auto leading-relaxed">
              {cityLabel ? (
                <>
                  Live, lender-direct rate intelligence for {cityLabel} buyers and
                  renewals. We don't quote last week's rates — we quote what
                  changed at 9:14 this morning, and tell you where they're
                  heading next.
                </>
              ) : (
                <>
                  The only Alberta broker with live, lender-direct rate intelligence. We
                  don't quote last week's rates — we quote what changed at 9:14 this
                  morning, and tell you where they're heading next.
                </>
              )}
            </p>

            {/* Persona buttons */}
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 max-w-2xl mx-auto">
              <PersonaCard
                icon={Home}
                title="I'm buying a home"
                subtitle="Purchase or pre-approval"
                tone="red"
                onClick={() =>
                  openDrawer({ mode: "full", presetLevel1: "buying" })
                }
                testid="hero-persona-buying"
              />
              <PersonaCard
                icon={RefreshCw}
                title="I'm renewing/refinancing"
                subtitle="Existing mortgage"
                tone="red"
                onClick={() =>
                  openDrawer({ mode: "full", presetLevel1: "renew_refi" })
                }
                testid="hero-persona-renew-refi"
              />
            </div>
          </section>

          {/* Featured Rates */}
          <div id={SCROLL_TARGET} className="scroll-mt-8">
            <FeaturedRates
              data={rates}
              loading={ratesLoading}
              onInquire={(card) =>
                openDrawer({ mode: "inquire", inquireContext: card })
              }
              onCompareAll={() => navigate(withRef("/compare-all"))}
            />
          </div>

          {/* Market Signal */}
          <MarketSignalCard signal={signal} loading={signalLoading} />

          {/* How It Works */}
          <HowItWorks onCta={() => openDrawer({ mode: "full" })} />

          {/* The Decision Desk — BoC Watch + Fixed vs Variable */}
          <DecisionDesk
            data={desk}
            loading={deskLoading}
            onCta={() => openDrawer({ mode: "full" })}
            onSwitchCta={() =>
              openDrawer({ mode: "full", presetIntent: "switch_to_fixed" })
            }
          />

          {/* Trust — standalone only */}
          {!isEmbed && <TrustSection onCta={() => openDrawer({ mode: "full" })} />}

          {/* Footer — standalone only */}
          {!isEmbed && <PublicFooter />}
        </div>
      </main>
      )}

      <FunnelDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        config={drawerConfig}
      />
    </>
  );
}

// ---------- Persona card (hero) -----------------------------------------
function PersonaCard({ icon: Icon, title, subtitle, tone, onClick, testid }) {
  const navy = tone === "navy";
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`group relative text-left p-5 sm:p-6 rounded-2xl transition-all border-2 hover:-translate-y-0.5 ${
        navy
          ? "bg-brand-navy text-white border-brand-navy hover:bg-brand-navy-hover"
          : "bg-white text-brand-navy border-slate-200 hover:border-brand-red hover:shadow-xl"
      }`}
    >
      <div className="flex items-start gap-4">
        <span
          className={`inline-flex items-center justify-center w-12 h-12 rounded-xl shrink-0 transition-transform group-hover:scale-105 ${
            navy ? "bg-white/15 text-white" : "bg-brand-red text-white"
          }`}
        >
          <Icon size={22} strokeWidth={2.5} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="text-lg sm:text-xl font-bold leading-tight font-display">
            {title}
          </div>
          <div className={`text-xs sm:text-sm mt-1 font-body ${navy ? "text-white/70" : "text-slate-500"}`}>
            {subtitle}
          </div>
        </div>
        <ArrowRight
          size={18}
          strokeWidth={2.5}
          className={`shrink-0 mt-1.5 transition-transform group-hover:translate-x-0.5 ${
            navy ? "text-white/70" : "text-slate-300 group-hover:text-brand-red"
          }`}
        />
      </div>
    </button>
  );
}

// ---------- How It Works -------------------------------------------------
function HowItWorks({ onCta }) {
  const steps = [
    {
      n: "1",
      title: "Tell us what you're looking for",
      text: "Buying or renewing — just a few quick details to get started.",
    },
    {
      n: "2",
      title: "Instantly see your real rates",
      text: "Matched to your situation using live lender data — not generic averages.",
    },
    {
      n: "3",
      title: "Know exactly what makes sense right now",
      text: "Which rate, which term — and what puts you in the best position.",
    },
  ];

  return (
    <section id="how-it-works" className="animate-fade-up text-center" data-testid="how-it-works">
      <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-brand-red mb-3 font-body">
        How it works
      </div>
      <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-brand-navy font-display mb-5">
        Get your personalized mortgage rate in under 60 seconds.
      </h2>

      {/* Trust bullets — counter the Ratehub hesitation */}
      <ul
        className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 mb-10 text-sm font-body text-slate-700"
        data-testid="how-it-works-trust"
      >
        <li className="inline-flex items-center gap-1.5">
          <Check size={16} strokeWidth={3} className="text-brand-red" />
          <span>No credit check</span>
        </li>
        <li className="inline-flex items-center gap-1.5">
          <Check size={16} strokeWidth={3} className="text-brand-red" />
          <span>No commitment</span>
        </li>
        <li className="inline-flex items-center gap-1.5">
          <Check size={16} strokeWidth={3} className="text-brand-red" />
          <span>Real lender rates</span>
        </li>
      </ul>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6 mb-8 text-left">
        {steps.map((s, i) => (
          <div
            key={s.n}
            className="bg-white border border-slate-200 rounded-2xl p-6 animate-fade-up"
            style={{ animationDelay: `${i * 100}ms` }}
          >
            <div className="text-5xl font-extrabold text-brand-red font-display tracking-tighter leading-none mb-4">
              {s.n}
            </div>
            <div className="text-lg font-bold text-brand-navy font-display mb-2">
              {s.title}
            </div>
            <p className="text-sm text-slate-600 font-body leading-relaxed">{s.text}</p>
          </div>
        ))}
      </div>

      <button
        type="button"
        onClick={onCta}
        data-testid="how-it-works-cta"
        className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-brand-red hover:bg-brand-red-hover text-white rounded-xl px-7 py-4 font-bold font-body transition-colors"
      >
        See My Personalized Rate
        <ArrowRight size={18} strokeWidth={2.5} />
      </button>
    </section>
  );
}

// ---------- The Decision Desk -----------------------------------------
function DecisionDesk({ data, loading, onCta, onSwitchCta }) {
  if (loading && !data) {
    return (
      <section
        className="animate-fade-up"
        data-testid="decision-desk-loading"
        aria-busy="true"
      >
        <div className="h-64 bg-slate-50 border border-slate-200 rounded-2xl animate-pulse" />
      </section>
    );
  }
  if (!data) return null;

  return (
    <section
      className="animate-fade-up space-y-6"
      data-testid="decision-desk"
    >
      <header className="text-center">
        <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-brand-red mb-3 font-body">
          The Decision Desk
        </div>
        <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-brand-navy font-display">
          What's actually happening with rates
        </h2>
        <p className="text-sm sm:text-base text-slate-600 mt-3 max-w-2xl mx-auto font-body">
          No jargon. No hedging. Just our honest read on today's market — and what it means for you.
        </p>
      </header>

      <BocWatchCard boc={data.boc} />
      <FixedVsVariableCard
        comparison={data.comparison}
        signal={data.signal}
        hikeTolerance={data.hike_tolerance}
        onCta={onCta}
      />
      <HaveVariableCta onCta={onSwitchCta || onCta} />
    </section>
  );
}

function BocWatchCard({ boc }) {
  const latest = boc?.latest;
  const next = boc?.next;

  const formatDate = (iso) => {
    if (!iso) return "—";
    try {
      return new Date(iso + "T12:00:00Z").toLocaleDateString("en-CA", {
        month: "long",
        day: "numeric",
        year: "numeric",
      });
    } catch {
      return iso;
    }
  };

  return (
    <div
      className="bg-white border border-slate-200 rounded-2xl p-6 sm:p-8"
      data-testid="boc-watch-card"
    >
      <div className="flex items-center gap-2 mb-5">
        <Calendar size={16} className="text-brand-red" strokeWidth={2.5} />
        <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-brand-red font-body">
          Bank of Canada Watch
        </span>
      </div>

      {latest ? (
        <div className="space-y-4">
          <div>
            <div className="text-xs uppercase tracking-wide text-slate-500 font-body mb-1">
              Latest decision · {formatDate(latest.date)}
            </div>
            <div className="text-xl sm:text-2xl font-bold text-brand-navy font-display">
              {latest.decision || (latest.rate ? `Target rate held at ${latest.rate}%` : "Decision details updating…")}
            </div>
          </div>

          {latest.forward_guidance && (
            <div
              className="bg-slate-50 border border-slate-200 rounded-xl p-4 sm:p-5"
              data-testid="boc-forward-guidance"
            >
              <div className="text-xs uppercase tracking-wide text-slate-500 font-bold font-body mb-2">
                The read · what they're signalling next
              </div>
              <p className="text-sm sm:text-base text-slate-800 font-body leading-relaxed whitespace-pre-line">
                {latest.forward_guidance}
              </p>
            </div>
          )}

          {latest.for_homeowners && (
            <div className="bg-slate-50 border-l-4 border-brand-red pl-4 py-3 rounded-r-lg">
              <div className="text-xs uppercase tracking-wide text-brand-red font-bold font-body mb-1">
                What it means for you
              </div>
              <p className="text-sm sm:text-base text-slate-800 font-body leading-relaxed">
                {latest.for_homeowners}
              </p>
            </div>
          )}
        </div>
      ) : (
        <p className="text-sm text-slate-500 font-body italic">
          Latest decision details are refreshing…
        </p>
      )}

      {next && (
        <div className="mt-6 pt-5 border-t border-slate-200 flex items-center justify-between flex-wrap gap-2">
          <div>
            <div className="text-xs uppercase tracking-wide text-slate-500 font-body mb-0.5">
              Next decision
            </div>
            <div className="text-lg font-bold text-brand-navy font-display">
              {formatDate(next.date)}
            </div>
          </div>
          <div
            className="inline-flex items-center gap-1.5 bg-brand-navy text-white px-3 py-1.5 rounded-full text-xs font-bold font-body"
            data-testid="boc-next-countdown"
          >
            {next.days_away <= 0
              ? "Today"
              : next.days_away === 1
              ? "Tomorrow"
              : `${next.days_away} days away`}
          </div>
        </div>
      )}
    </div>
  );
}

function FixedVsVariableCard({ comparison, signal, hikeTolerance, onCta }) {
  const fmt = (n) => (n == null ? "—" : `${Number(n).toFixed(2)}%`);
  const fmtMoney = (n) =>
    n == null ? "—" : `$${Math.round(n).toLocaleString("en-CA")}`;

  // Panel chrome stays neutral slate regardless of the verdict — the
  // verdict itself is communicated by the headline copy and the
  // top-right pill, so the panel doesn't need to flash green/amber and
  // dominate the page. Keeps the card readable and on-brand.
  const leanTheme = {
    label: (
      { LEAN_VARIABLE: "Variable wins today", LEAN_FIXED: "Lock in fixed today", NEUTRAL: "Too close to call" }
    )[signal?.lean || "NEUTRAL"],
    bg: "bg-slate-50",
    border: "border-slate-400",
    text: "text-slate-800",
    pill: "bg-slate-500",
    icon: { LEAN_VARIABLE: TrendingDown, LEAN_FIXED: TrendingUp, NEUTRAL: Minus }[signal?.lean || "NEUTRAL"] || Minus,
  };

  const LeanIcon = leanTheme.icon;

  return (
    <div
      className="bg-white border border-slate-200 rounded-2xl p-6 sm:p-8"
      data-testid="fixed-vs-variable-card"
    >
      <div className="flex items-center gap-2 mb-5">
        <Scale size={16} className="text-brand-red" strokeWidth={2.5} />
        <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-brand-red font-body">
          Fixed vs Variable · Right Now
        </span>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:gap-6 mb-6">
        <div
          className="bg-slate-50 border border-slate-200 rounded-xl p-4 sm:p-5"
          data-testid="compare-fixed"
        >
          <div className="text-xs uppercase tracking-wide text-slate-500 font-body mb-1">
            {comparison.fixed.label}
          </div>
          <div className="text-2xl sm:text-3xl font-bold text-brand-navy font-display">
            {fmt(comparison.fixed.rate)}
          </div>
          <div className="text-xs text-slate-500 mt-1 font-body">
            {fmtMoney(comparison.fixed.monthly_payment)}/month
          </div>
        </div>

        <div
          className="bg-slate-50 border border-slate-200 rounded-xl p-4 sm:p-5"
          data-testid="compare-variable"
        >
          <div className="text-xs uppercase tracking-wide text-slate-500 font-body mb-1">
            {comparison.variable.label}
          </div>
          <div className="text-2xl sm:text-3xl font-bold text-brand-navy font-display">
            {fmt(comparison.variable.rate)}
          </div>
          <div className="text-xs text-slate-500 mt-1 font-body">
            {fmtMoney(comparison.variable.monthly_payment)}/month
          </div>
        </div>
      </div>

      <div className="text-center text-xs text-slate-500 font-body mb-6">
        Based on a ${comparison.reference_loan.toLocaleString("en-CA")} mortgage · {comparison.amort_years}-year amortization
      </div>

      <div
        className={`border-l-4 ${leanTheme.border} ${leanTheme.bg} rounded-r-xl p-5 sm:p-6 space-y-4`}
        data-testid="todays-call"
      >
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="text-xs uppercase tracking-wide text-slate-600 font-bold font-body mb-1">
              Today's Call
            </div>
            <div className={`text-xl sm:text-2xl font-bold ${leanTheme.text} font-display`}>
              {leanTheme.label}
            </div>
          </div>
          <div className={`inline-flex items-center gap-1.5 ${leanTheme.pill} text-white px-3 py-1.5 rounded-full text-xs font-bold font-body`}>
            <LeanIcon size={14} strokeWidth={2.5} />
            {signal?.lean?.replace("LEAN_", "") || "NEUTRAL"}
          </div>
        </div>

        {signal?.headline && (
          <p className={`text-base sm:text-lg font-bold ${leanTheme.text} font-body leading-snug`}>
            {signal.headline}
          </p>
        )}

        {signal?.reasons?.length > 0 && (
          <ul className="space-y-3" data-testid="signal-reasons">
            {signal.reasons.map((r, i) => (
              <li key={i} className="flex items-start gap-2.5 text-sm text-slate-700 font-body leading-relaxed">
                <span className={`${leanTheme.pill} w-1.5 h-1.5 rounded-full mt-2 shrink-0`} />
                <span>{r}</span>
              </li>
            ))}
          </ul>
        )}

        <HikeToleranceBlock data={hikeTolerance} />
      </div>

      <div className="mt-6 flex justify-center">
        <button
          type="button"
          onClick={onCta}
          data-testid="decision-desk-cta"
          className="inline-flex items-center justify-center gap-2 bg-brand-red hover:bg-brand-red-hover text-white rounded-xl px-6 py-3 font-bold font-body transition-colors text-sm"
        >
          See My Personalized Rate
          <ArrowRight size={16} strokeWidth={2.5} />
        </button>
      </div>
    </div>
  );
}

// ---- Hike-tolerance stress test ----------------------------------------
//
// Pure-math block under "Today's Call". Answers "how many quarter-point
// BoC hikes would the variable rate need to absorb before fixed wins on
// cumulative interest over the 5-yr term?" — front-loaded in year 1
// (worst-case timing). Backed by `compute_hike_tolerance()` in
// `switch_calculator.py`. Hidden when not applicable (variable already
// more expensive than fixed) or when the threshold is capped at 12+
// (practically impossible).
function HikeToleranceBlock({ data }) {
  if (!data || !data.applicable) return null;
  const n = data.hikes_until_fixed_wins;
  const term = data.term_years || 5;
  if (data.capped) {
    return (
      <div
        className="mt-3 pt-4 border-t border-slate-200/70"
        data-testid="hike-tolerance-block"
      >
        <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500 mb-2 font-body">
          Stress test · how much room you have
        </div>
        <p className="text-sm sm:text-base text-slate-800 font-body leading-relaxed">
          Even <strong>12+ quarter-point hikes</strong> in year 1 wouldn't be enough
          for fixed to catch up over the next {term} years. The cushion variable
          gives you today is unusually large.
        </p>
      </div>
    );
  }
  const timesWord = n === 1 ? "1 time" : `${n} times`;
  return (
    <div
      className="mt-3 pt-4 border-t border-slate-200/70"
      data-testid="hike-tolerance-block"
    >
      <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500 mb-2 font-body">
        Stress test · how much room you have
      </div>
      <p className="text-sm sm:text-base text-slate-800 font-body leading-relaxed">
        The Bank of Canada would need to increase the variable rate{" "}
        <strong>0.25%, {timesWord}</strong> over the next {term} years in order
        for you to save interest by taking a fixed rate mortgage.
      </p>
      <p className="text-xs sm:text-sm text-slate-600 font-body leading-relaxed mt-3">
        <span className="font-bold text-slate-700">For context:</span> the Bank
        of Canada raised rates by <strong>4.75%</strong> from 2022 to 2023 —
        roughly 19 quarter-point steps in 16 months. That was the steepest
        hiking cycle in over 30 years.
      </p>
      <p className="text-xs text-slate-500 font-body italic mt-2">
        Math assumes hikes hit early (year one) — the most punishing timing.
        Spread across the term, variable's lead grows.
      </p>
    </div>
  );
}

function HaveVariableCta({ onCta }) {
  return (
    <div
      className="bg-brand-navy text-white rounded-2xl p-6 sm:p-8"
      data-testid="have-variable-cta"
    >
      <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-white/60 mb-3 font-body">
        Already in a variable rate?
      </div>
      <h3 className="text-2xl sm:text-3xl font-bold tracking-tight font-display leading-tight mb-3">
        Should you switch to fixed — or stay put?
      </h3>
      <p className="text-sm sm:text-base text-white/80 font-body leading-relaxed mb-5 max-w-2xl">
        The right answer depends on <em>your</em> rate and balance — not averages. We'll run the exact math on your mortgage: what your payment looks like today, what it becomes if the Bank of Canada moves, and whether locking today actually saves you money.
      </p>
      <button
        type="button"
        onClick={onCta}
        data-testid="have-variable-cta-btn"
        className="inline-flex items-center gap-2 bg-white text-brand-navy hover:bg-slate-100 rounded-xl px-6 py-3 font-bold font-body transition-colors text-sm"
      >
        Run my numbers
        <ArrowRight size={16} strokeWidth={2.5} />
      </button>
      <div className="text-xs text-white/60 mt-3 font-body">
        Free. No credit check. Takes about 60 seconds.
      </div>
    </div>
  );
}



// ---------- Trust --------------------------------------------------------
//
// "Say Hello To The Easiest Way To Mortgage" — broker-led pitch with
// 4 differentiator cards (Alberta-based, 50+ lenders, RateWatch+,
// Personalized strategy), an auto-scrolling lender-logo marquee
// pulling live from /api/public/lender-logos, and an Elfsight
// testimonials slider widget. Stats strip removed per broker direction
// — feature cards do the work alone.
function TrustSection({ onCta }) {
  return (
    <section className="animate-fade-up" data-testid="trust-section">
      <div className="text-center max-w-3xl mx-auto mb-10 sm:mb-14">
        <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-brand-red mb-3 font-body">
          Trusted by Alberta homeowners
        </div>
        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-brand-navy font-display mb-5 leading-[1.1]">
          Say Hello To The Easiest Way To Mortgage
        </h2>
        <p className="text-base sm:text-lg text-slate-600 font-body leading-relaxed mb-6">
          Hello Mortgage is more than just a place to get a rate. We help you
          understand your options, build the right strategy, and move through
          the mortgage process with confidence.
        </p>
        <button
          type="button"
          onClick={onCta}
          data-testid="trust-cta-btn"
          className="inline-flex items-center gap-2 text-brand-red hover:text-brand-red-hover font-bold font-body text-base transition-colors group"
        >
          Get started
          <ArrowRight size={16} strokeWidth={2.5} className="group-hover:translate-x-0.5 transition-transform" />
        </button>
      </div>

      {/* 4 differentiator cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5 mb-10 sm:mb-14">
        <FeatureCard
          icon={MapPin}
          kicker="Alberta-based"
          title="Mortgage Expertise"
          body="Work with a licensed Alberta broker who understands the local market, lender guidelines, and the strategies that matter for Alberta homeowners."
          testid="feature-alberta"
        />
        <FeatureCard
          icon={Building2}
          kicker="50+ Lenders"
          title="Banks, Monolines & More"
          body="We compare 50+ lenders — major banks, credit unions, and monoline lenders — so you're never stuck with one institution's policies."
          testid="feature-lenders"
        />
        <FeatureCard
          icon={Eye}
          kicker="RateWatch+"
          title="More Than Just A Rate Hold"
          body="When a lender's rate drops after approval, they don't offer you the lower one. RateWatch+ checks your lender continuously until closing, and when it drops, we negotiate the lower rate for you."
          testid="feature-ratewatch"
        />
        <FeatureCard
          icon={Compass}
          kicker="Personalized Strategy"
          title="Built Around Your Goals"
          body="Your mortgage should support your bigger financial picture. Tailored advice for first-time buyers, renewals, refinances, debt consolidation, and long-term wealth-building through homeownership."
          testid="feature-strategy"
        />
      </div>

      {/* Lender logo marquee — pulls live from the lenders DB */}
      <LenderMarquee />

      {/* Testimonials — Elfsight slider widget */}
      <ElfsightTestimonials />
    </section>
  );
}

function FeatureCard({ icon: Icon, kicker, title, body, testid }) {
  return (
    <div
      data-testid={testid}
      className="bg-white border border-slate-200 rounded-2xl p-6 hover:border-brand-navy/30 hover:shadow-md transition-all"
    >
      <div className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-brand-red/10 text-brand-red mb-4">
        <Icon size={18} strokeWidth={2} />
      </div>
      <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-brand-red mb-1.5 font-body">
        {kicker}
      </div>
      <h3 className="text-lg font-bold text-brand-navy font-display mb-2 leading-tight">
        {title}
      </h3>
      <p className="text-sm text-slate-600 font-body leading-relaxed">{body}</p>
    </div>
  );
}

// Auto-scrolling logo marquee. Two copies of the logo strip are placed
// side-by-side in a flex row that animates -50% on the X axis — when
// the first copy fully exits stage-left, the second copy is exactly in
// position, creating an infinite seamless loop. Hover pauses for users
// who want to read a specific logo.
function LenderMarquee() {
  const [logos, setLogos] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await publicApi.get("/lender-logos");
        if (!cancelled) setLogos(r.data?.lenders || []);
      } catch {
        /* non-fatal — section just renders without the marquee */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (!logos.length) return null;

  // Duplicate the strip so the CSS animation can loop seamlessly
  const strip = [...logos, ...logos];

  return (
    <div
      className="mb-12 sm:mb-16 py-6 border-y border-slate-200"
      data-testid="lender-marquee"
    >
      <div className="text-center text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400 mb-5 font-body">
        Some of the lenders we work with
      </div>
      <div className="overflow-hidden relative group">
        {/* Edge fades — soften the marquee against the page background */}
        <div className="absolute inset-y-0 left-0 w-12 sm:w-20 bg-gradient-to-r from-neutral-50 to-transparent z-10 pointer-events-none" />
        <div className="absolute inset-y-0 right-0 w-12 sm:w-20 bg-gradient-to-l from-neutral-50 to-transparent z-10 pointer-events-none" />
        <div
          className="flex items-center gap-12 sm:gap-16 animate-marquee group-hover:[animation-play-state:paused]"
          style={{ width: "max-content" }}
        >
          {strip.map((l, i) => (
            <div
              key={`${l.name}-${i}`}
              className="shrink-0 h-12 sm:h-14 flex items-center justify-center"
              title={l.name}
            >
              <img
                src={l.logo_url}
                alt={l.name}
                loading="lazy"
                className="h-full w-auto object-contain max-w-[160px] grayscale opacity-70 hover:grayscale-0 hover:opacity-100 transition-all"
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// Elfsight third-party testimonials slider. The platform.js script is
// loaded once globally via a ref to a top-level <script> element so
// multiple section instances don't double-inject. The widget itself is
// a div with a fixed app-id class that elfsight platform.js hydrates
// on its own via MutationObserver.
function ElfsightTestimonials() {
  useEffect(() => {
    const SRC = "https://elfsightcdn.com/platform.js";
    if (document.querySelector(`script[src="${SRC}"]`)) {
      // Already loaded — Elfsight watches for new app divs and hydrates them automatically.
      return;
    }
    const s = document.createElement("script");
    s.src = SRC;
    s.async = true;
    document.head.appendChild(s);
  }, []);

  return (
    <div className="max-w-4xl mx-auto" data-testid="testimonials-slider">
      <div className="text-center mb-6">
        <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-brand-red mb-2 font-body">
          What our clients say
        </div>
      </div>
      <div
        className="elfsight-app-2f35752d-918f-4f1d-9183-11514811569a"
        data-elfsight-app-lazy
      />
    </div>
  );
}
